import React, { FC, useCallback, useContext } from 'react';
import { useIntl } from 'react-intl';
import { Query } from '@material-table/core';
import { MTable, Form } from '../../../../Components';
import { FormValues, FormFieldValueType } from '../../../../Components/Form/Form.types';
import { useFormField } from '../../../../Components/Form/FormField';
import { AptorApi, useAptorApi, useFetchSelectOptions } from '../../../../Api';
import {
  INamedEntity,
  IListResult,
  IGridQueryResult,
  IGridQueryRequest,
  OrderDirection,
} from '../../../../Api/AptorApi';
import { useStyles } from '../Organization/Organization.styles';
import { UserContext } from '../../../../Context/UserContext/UserContext';

export interface ITag extends INamedEntity {}

interface IProps {
  optionFetcher: () => Promise<IListResult<INamedEntity>>;
  dataFetcher: (request: IGridQueryRequest) => Promise<IGridQueryResult<ITag>>;
  addNew: (api: AptorApi, data: FormValues) => Promise<void>;
  remove: (api: AptorApi, tagid: number) => Promise<void>;
}

export const Tags: FC<IProps> = ({ optionFetcher, dataFetcher, addNew, remove }) => {
  const { api, abortController } = useAptorApi();
  const tableRef = React.useRef<any>(null);
  const { formatMessage } = useIntl();
  const { getTagName } = useContext(UserContext);
  const classes = useStyles();
  const [tags, , , refetch] = useFetchSelectOptions(optionFetcher, abortController);

  const newTagField = useFormField({
    label: formatMessage({ id: 'law-portal.manage.edit-law.addTag' }),
    name: 'tagId',
    type: 'single-select',
    options: tags,
    required: true,
  });
  const setTag = newTagField.setValue;

  const handleDataFetch = useCallback(
    async (query: Query<ITag>) => {
      return await dataFetcher({
        orderDirection: query.orderDirection === 'desc' ? OrderDirection.Descending : OrderDirection.Ascending,
        orderBy: query.orderBy !== undefined ? query.orderBy.field : 'name',
        page: query.page,
        pageSize: query.pageSize,
        search: query.search,
      } as IGridQueryRequest);
    },
    [dataFetcher],
  );

  const handleAddTag = async (data: FormValues, api: AptorApi, onSuccess: () => void) => {
    await addNew(api, { ...data, tags: [data.tagId as FormFieldValueType] });
    if (api.abortController?.signal.aborted) {
      return;
    }

    onSuccess();
    (tableRef.current as any).onQueryChange();
    setTag(undefined);
    refetch();
  };

  const removeTag = (tag: ITag) => ({
    tooltip: formatMessage({ id: 'component.mtable.deleteTooltip' }),
    icon: 'delete',
    onClick: async () => {
      await remove(api, tag.id);
      if (abortController.current.signal.aborted) {
        return;
      }
      (tableRef.current as any).onQueryChange();
      refetch();
    },
  });

  return (
    <>
      <Form
        className={classes.form}
        singleField={newTagField}
        submit={handleAddTag}
        submitLabel={formatMessage({ id: 'form.add' })}
      />

      <MTable
        // hideToolbar
        tableRef={tableRef}
        // columns={[{ title: formatMessage({ id: 'organization.tags' }), field: 'name' }]}
        columns={[{ title: getTagName(formatMessage), field: 'name' }]}
        data={handleDataFetch}
        actions={[removeTag]}
        components={{}}
      />
    </>
  );
};
